import React from 'react';

/**
 * Renders the Markdown subset the platform's AI analyst produces.
 *
 * Builds React elements rather than injecting an HTML string. Everything here
 * is model output, so `dangerouslySetInnerHTML` would put an XSS surface one
 * prompt-injection away from a user's browser — a page whose whole job is
 * reporting on prompt injection should not carry that.
 *
 * Also safe to call on a half-finished string: answers stream in token by
 * token, so this is asked to render `"**14** of **2"` many times per answer.
 * Unclosed delimiters stay literal instead of swallowing the rest of the text.
 *
 * Deliberately NOT general-purpose Markdown. Underscore emphasis is
 * unsupported, because platform content is full of snake_case
 * (`min_critical_agents`) and `_italics_` would eat it.
 */

// One pass, alternation ordered so `**` is tried before `*`.
const INLINE = /(\*\*)([\s\S]+?)\*\*|(\*)([^*\s][\s\S]*?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Only http(s) and in-app paths. Never javascript:, data:, or similar. */
const safeHref = (href) => (/^(https?:\/\/|\/)/i.test(href || '') ? href : null);

export const renderInline = (text, keyPrefix = 'i') => {
  const nodes = [];
  let last = 0;
  let match;
  INLINE.lastIndex = 0;

  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${match.index}`;

    if (match[1]) nodes.push(<strong key={key}>{match[2]}</strong>);
    else if (match[3]) nodes.push(<em key={key}>{match[4]}</em>);
    else if (match[5]) nodes.push(<code key={key}>{match[5]}</code>);
    else if (match[6]) {
      const href = safeHref(match[7]);
      nodes.push(
        href
          ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{match[6]}</a>
          : match[6],   // a link we will not follow still shows its text
      );
    }
    last = match.index + match[0].length;
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const LIST_ITEM = /^(\s*)(\d+[.)]|[-*+•])\s+(.*)$/;
const BLOCK_START = /^(#{1,6}\s|\||```|>)/;
const indentOf = (line) => line.match(/^\s*/)[0].replace(/\t/g, '    ').length;
const isOrderedMarker = (marker) => /^\d/.test(marker);

/**
 * One list starting at ``lines[start]``. An item's indented lines continue it;
 * an indented marker opens a child list; text after a child list stays in the
 * parent item. Unindented wrapped lines (streamed answers) still continue the
 * item above. Returns the rendered list and the index of the next line.
 */
const parseList = (lines, start, keyPrefix) => {
  const [, , firstMarker] = LIST_ITEM.exec(lines[start]);
  const indent = indentOf(lines[start]);
  const ordered = isOrderedMarker(firstMarker);
  const items = [];               // each item: [{ text } | { list }]
  let i = start;

  const addText = (text) => {
    const parts = items[items.length - 1];
    const last = parts[parts.length - 1];
    if (last && 'text' in last) last.text += ` ${text}`;
    else parts.push({ text });
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    const lineIndent = indentOf(line);
    const item = LIST_ITEM.exec(line);

    if (!trimmed) {
      // A blank line ends the list unless the list carries on right after it.
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j += 1;
      const next = LIST_ITEM.exec(lines[j] || '');
      const nextIndent = indentOf(lines[j] || '');
      const carriesOn = next && items.length && (
        nextIndent > indent || (nextIndent === indent && isOrderedMarker(next[2]) === ordered)
      );
      if (!carriesOn) break;
      i = j;
      continue;
    }

    if (item && lineIndent > indent && items.length) {
      const child = parseList(lines, i, `${keyPrefix}-${items.length}`);
      items[items.length - 1].push({ list: child.node });
      i = child.next;
      continue;
    }
    if (item) {
      if (lineIndent < indent || isOrderedMarker(item[2]) !== ordered) break;
      items.push([{ text: item[3] }]);
      i += 1;
      continue;
    }
    const continues = items.length && !BLOCK_START.test(trimmed)
      && (lineIndent > indent || (indent === 0 && lineIndent === 0));
    if (!continues) break;
    addText(trimmed);
    i += 1;
  }

  const startAt = ordered ? parseInt(firstMarker, 10) : 1;
  const node = React.createElement(
    ordered ? 'ol' : 'ul',
    { key: keyPrefix, ...(startAt !== 1 ? { start: startAt } : {}) },
    items.map((parts, n) => (
      <li key={n}>
        {parts.map((part, p) => {
          if (part.list) return <React.Fragment key={p}>{part.list}</React.Fragment>;
          const inline = renderInline(part.text, `${keyPrefix}-${n}-${p}`);
          // Text that follows a nested list is its own line inside the item.
          return p === 0 ? <React.Fragment key={p}>{inline}</React.Fragment> : <div key={p}>{inline}</div>;
        })}
      </li>
    )),
  );
  return { node, next: i };
};

const isTableRow = (line) => line.startsWith('|') && line.endsWith('|');
const isTableRule = (line) => /^\|[\s:|-]+\|$/.test(line) && line.includes('-');
const splitRow = (line) => line.slice(1, -1).split('|').map((c) => c.trim());

/** Group lines into blocks, then render each. */
const renderBlocks = (src) => {
  const lines = String(src).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let para = [];

  const flushParagraph = () => {
    if (!para.length) return;
    out.push(<p key={`p-${out.length}`}>{renderInline(para.join(' '), `p${out.length}`)}</p>);
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { flushParagraph(); i += 1; continue; }

    // Fenced code — kept verbatim, including a fence the stream has not closed.
    if (trimmed.startsWith('```')) {
      flushParagraph();
      const body = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { body.push(lines[i]); i += 1; }
      i += 1;
      out.push(<pre key={`c-${out.length}`}><code>{body.join('\n')}</code></pre>);
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      const level = Math.min(6, heading[1].length + 2);   // # renders as h3, never h1
      out.push(React.createElement(
        `h${level}`, { key: `h-${out.length}` }, renderInline(heading[2], `h${out.length}`),
      ));
      i += 1;
      continue;
    }

    if (isTableRow(trimmed) && isTableRule((lines[i + 1] || '').trim())) {
      flushParagraph();
      const head = splitRow(trimmed);
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i].trim())) {
        rows.push(splitRow(lines[i].trim()));
        i += 1;
      }
      out.push(
        <div className="md-table-wrap" key={`t-${out.length}`}>
          <table>
            <thead>
              <tr>{head.map((c, n) => <th key={n}>{renderInline(c, `th${n}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>{row.map((c, n) => <td key={n}>{renderInline(c, `td${r}-${n}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (LIST_ITEM.test(line)) {
      flushParagraph();
      const list = parseList(lines, i, `l-${out.length}`);
      out.push(list.node);
      i = list.next;
      continue;
    }

    if (trimmed === '>' || trimmed.startsWith('> ')) {
      flushParagraph();
      // Consecutive quote lines are one quote.
      const quote = [];
      while (i < lines.length && (lines[i].trim() === '>' || lines[i].trim().startsWith('> '))) {
        quote.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      out.push(
        <blockquote key={`q-${out.length}`}>
          {renderInline(quote.join(' ').trim(), `q${out.length}`)}
        </blockquote>,
      );
      continue;
    }

    para.push(trimmed);
    i += 1;
  }

  flushParagraph();
  return out;
};

const MarkdownText = ({ children, className = '' }) => (
  <div className={`md ${className}`.trim()}>{renderBlocks(children || '')}</div>
);

export default MarkdownText;
